import React from "react";
import SEO from "../../../components/SEO";
import img96 from "../../../assets/img96.jpg";
import { CheckCircle, Check } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function RegisteredDisabilitySavingsPlan() {
  const container = "w-full max-w-[1380px] mx-auto";

  return (
    <>
      <SEO
        title="Régime enregistré d'épargne-invalidité (REEI) – einsured.ca"
        description="Découvrez le REEI : critères d’admissibilité, subventions gouvernementales et avantages des cotisations pour renforcer votre sécurité financière à long terme."
        keywords="REEI Canada, Régime enregistré d'épargne-invalidité, subvention épargne-invalidité, bons REEI, admissibilité crédit impôt invalidité Canada"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img96})` }}
      >
        <div className="absolute inset-0 bg-black/55" />

        <div className={container}>
          <div className="relative z-10 text-white max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 leading-tight">
              Régime enregistré <br /> d'épargne-invalidité
            </h1>

            <p className="text-base sm:text-lg font-medium mb-6">
              Jusqu'à 90 000 $ en subventions et bons gouvernementaux
            </p>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto bg-[#00b4ff] hover:bg-[#00a3e6] text-white font-semibold px-10 py-3 rounded-full transition shadow-md text-base sm:text-lg min-h-[48px]"
              >
                Commencer
              </a>

              <a
                href="/fr/calculateur-reei"
                className="inline-flex items-center justify-center w-full sm:w-auto bg-[#00b4ff] hover:bg-[#00a3e6] text-white font-semibold px-10 py-3 rounded-full transition shadow-md text-base sm:text-lg min-h-[48px]"
              >
                Calculateur REEI einsured.ca
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* RDSP + WHAT IS RDSP */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              Régime enregistré d'épargne-invalidité (REEI)
            </h2>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
              Conseils experts et personnalisés sur le Régime enregistré d'épargne-invalidité
              (REEI) — aidant les familles canadiennes à maximiser les avantages, les
              subventions et la sécurité financière à long terme.
            </p>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-10">
              Chez einsured.ca, nous simplifions la planification du REEI et la rendons
              sans stress. Que vous débutiez ou que vous souhaitiez tirer davantage parti
              de votre régime existant, nos conseillers expérimentés vous guident à chaque
              étape — afin que vos proches bénéficient du soutien qu'ils méritent.
            </p>

            {/* What is an RDSP */}
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
              Qu'est-ce qu'un REEI ?
            </h3>

            <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8">
              Un Régime enregistré d'épargne-invalidité (REEI) est un puissant outil
              d'épargne conçu par le gouvernement canadien pour aider les personnes
              handicapées et leurs familles à bâtir une sécurité financière à long terme.
              Grâce à une croissance à imposition différée et à de généreuses subventions
              et bons gouvernementaux, un REEI peut faire une différence déterminante.
            </p>

            {/* Benefits */}
            <div className="grid gap-10 md:grid-cols-2 items-center">
              {/* Left: Key benefits */}
              <div>
                <h4 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-5">
                  Principaux avantages :
                </h4>

                <ul className="space-y-4 text-base sm:text-lg">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                    <span>Croissance des placements à imposition différée</span>
                  </li>

                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                    <span>Jusqu'à 90 000 $ en subventions et bons gouvernementaux</span>
                  </li>

                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                    <span>Cotisations flexibles de la famille et des amis</span>
                  </li>

                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                    <span>Soutien financier sécurisé à long terme</span>
                  </li>
                </ul>
              </div>

              {/* Right: $90,000 callout */}
              <div className="text-center md:text-left">
                <div className="text-[58px] sm:text-[74px] md:text-[84px] font-semibold leading-none text-[#20a7df] break-words">
                  90 000 $
                </div>
                <div className="text-base sm:text-xl text-gray-700 mt-3">
                  Subventions et bons gouvernementaux
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW RDSPS WORK */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4">
              Comment fonctionnent les REEI
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-10">
              Étape par étape :
            </p>

            <div className="space-y-6 sm:space-y-7">
              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-6 sm:px-10 py-7 sm:py-8 text-[#49628b] font-semibold text-base sm:text-xl md:text-2xl leading-relaxed">
                1. Ouvrir un compte REEI auprès d'une institution financière participante
              </div>

              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-6 sm:px-10 py-7 sm:py-8 text-[#49628b] font-semibold text-base sm:text-xl md:text-2xl leading-relaxed">
                2. Effectuer des cotisations (aucune limite annuelle ; maximum à vie de 200 000 $)
              </div>

              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-6 sm:px-10 py-7 sm:py-8 text-[#49628b] font-semibold text-base sm:text-xl md:text-2xl leading-relaxed">
                3. Demander la Subvention canadienne pour l'épargne-invalidité et le Bon canadien pour l'épargne-invalidité
              </div>

              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-6 sm:px-10 py-7 sm:py-8 text-[#49628b] font-semibold text-base sm:text-xl md:text-2xl leading-relaxed">
                4. Investir votre épargne pour une croissance à imposition différée
              </div>

              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-6 sm:px-10 py-7 sm:py-8 text-[#49628b] font-semibold text-base sm:text-xl md:text-2xl leading-relaxed">
                5. Retirer des fonds pour des besoins liés à l'invalidité (sous réserve de règles)
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAXIMIZE YOUR RDSP */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-4">
              Maximisez votre REEI : Subventions et bons
            </h2>

            <p className="text-base sm:text-lg text-gray-700 mb-12">
              Le gouvernement canadien offre deux incitatifs majeurs :
            </p>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-2 max-w-5xl mx-auto">
              {/* Grant card */}
              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-8 sm:px-10 py-10 sm:py-12 text-left">
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  <span className="font-bold">
                    Subvention canadienne pour l'épargne-invalidité :
                  </span>{" "}
                  Correspond aux cotisations jusqu'à 3 500 $/an, maximum à vie de 70 000 $
                </p>
              </div>

              {/* Bond card */}
              <div className="bg-[#f4f6f9] border border-black/10 rounded-[2rem] shadow-lg px-8 sm:px-10 py-10 sm:py-12 text-left">
                <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                  <span className="font-bold">
                    Bon canadien pour l'épargne-invalidité :
                  </span>{" "}
                  Jusqu'à 1 000 $/an pour les familles à faible revenu, maximum à vie de 20 000 $
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-12 py-4 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto min-h-[48px]"
              >
                Commencer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TIPS SECTION */}
      <section
        className="relative py-20 sm:py-24 md:py-20 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className={container}>
          <div className="relative z-10 text-white">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-12">
              Conseils :
            </h2>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3 max-w-6xl mx-auto">
              {/* Card 1 */}
              <div className="bg-white text-gray-800 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-10 sm:py-12 text-center border border-black/10">
                <div className="flex justify-center mb-6">
                  <Check className="h-14 w-14 text-[#00b4ff]" strokeWidth={4} />
                </div>
                <p className="text-base sm:text-lg leading-relaxed">
                  Demandez tôt, même avec de petites cotisations, pour maximiser les subventions et les bons
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-white text-gray-800 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-10 sm:py-12 text-center border border-black/10">
                <div className="flex justify-center mb-6">
                  <Check className="h-14 w-14 text-[#00b4ff]" strokeWidth={4} />
                </div>
                <p className="text-base sm:text-lg leading-relaxed">
                  Travaillez avec un conseiller qualifié
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-white text-gray-800 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-10 sm:py-12 text-center border border-black/10">
                <div className="flex justify-center mb-6">
                  <Check className="h-14 w-14 text-[#00b4ff]" strokeWidth={4} />
                </div>
                <p className="text-base sm:text-lg leading-relaxed">
                  Vérifiez l'admissibilité chaque année
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OUR PERSONALIZED RDSP PLANNING APPROACH */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className={container}>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#49628b] text-center mb-4">
              Notre approche personnalisée de planification du REEI
            </h2>

            <p className="text-base sm:text-lg  mb-6 mt-10">
              Chez einsured.ca, nous comprenons que les besoins de chaque famille sont uniques. Nos conseillers :
            </p>

            <div className="max-w-3xl mx-auto space-y-6 sm:space-y-7">
              {/* Item 1 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg">
                  Comparent les fournisseurs de REEI pour trouver la meilleure option
                </p>
              </div>

              {/* Item 2 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg">
                  Vous guident dans la paperasse et les critères d'admissibilité
                </p>
              </div>

              {/* Item 3 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg">
                  Vous aident à maximiser les prestations gouvernementales
                </p>
              </div>

              {/* Item 4 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg">
                  Offrent un soutien continu et des révisions périodiques
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <div className="mt-14 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-12 py-4 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-base sm:text-lg shadow-md transition w-full sm:w-auto min-h-[48px]"
              >
                Planifier votre REEI
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US? */}
      <section className="bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-16 px-4 sm:px-6 md:px-20 text-gray-800 mt-2">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#49628b] mb-10">
              Pourquoi nous choisir ?
            </h2>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              {/* Card 1 */}
              <div className="bg-white rounded-[1.75rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8 border border-black/10">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Service personnalisé et centré sur l'humain
                  </p>
                </div>
              </div>

              {/* Card 2 */}
              <div className="bg-white rounded-[1.75rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8 border border-black/10">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Rendez-vous en soirée et à distance
                  </p>
                </div>
              </div>

              {/* Card 3 */}
              <div className="bg-white rounded-[1.75rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8 border border-black/10">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                  <p className="text-base sm:text-lg leading-relaxed">
                    Soutien bilingue
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMMON RDSP MISTAKES & HOW TO AVOID THEM */}
      <section className="bg-white py-16 sm:py-16 px-4 sm:px-6 md:px-20 text-gray-800 mt-10">
        <div className={container}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#49628b] mb-10">
              Erreurs courantes avec le REEI et comment les éviter
            </h2>

            <div className="max-w-4xl mx-auto space-y-5 sm:space-y-6">
              {/* Item 1 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  Rater des subventions en retardant les cotisations
                </p>
              </div>

              {/* Item 2 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  Ne pas vérifier régulièrement l'admissibilité au crédit d'impôt pour personnes handicapées
                </p>
              </div>

              {/* Item 3 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  Retirer des fonds trop tôt (peut entraîner un remboursement)
                </p>
              </div>

              {/* Item 4 */}
              <div className="flex items-start gap-4">
                <CheckCircle className="h-5 w-5 text-sky-500 mt-1 flex-shrink-0" />
                <p className="text-base sm:text-lg leading-relaxed">
                  Ne pas réviser le régime annuellement
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOOK YOUR FREE RDSP CONSULTATION */}
      <section className="bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-16 px-4 sm:px-6 md:px-20 text-gray-800 mt-10">
        <div className={container}>
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] mb-10">
              Réservez votre consultation REEI gratuite
            </h2>

            {/* Info Card */}
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md border border-black/10 px-6 sm:px-10 py-8 sm:py-10">
              <p className="text-base sm:text-lg leading-relaxed text-gray-800">
                Prêt à commencer ou avez-vous des questions ? Réservez dès aujourd'hui une
                consultation gratuite et sans engagement avec nos experts en REEI.
              </p>
            </div>

            {/* CTA Button */}
            <div className="mt-10 flex justify-center">
              <a
                href="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-12 py-4 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-base sm:text-lg shadow-md transition w-full sm:w-auto min-h-[48px]"
              >
                Réserver maintenant
              </a>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles de blogue" mode="related" relatedCount={3} />
    </>
  );
}
